import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

/**
 * Renders only the rows currently on screen, so a 5,000 row import preview
 * scrolls without freezing the browser.
 *
 * Row heights vary (a flagged row carries its error messages underneath), so
 * the caller supplies an exact height per row and this keeps a running offset
 * table. Heights are known from the data before anything is drawn, which is
 * what lets the window be exact rather than estimated.
 */
export function RowWindow<T>({
  items,
  heightOf,
  render,
  maxHeight,
  overscan = 8,
  label,
}: {
  items: T[]
  heightOf: (item: T) => number
  render: (item: T, index: number) => ReactNode
  maxHeight: number
  overscan?: number
  label: string
}) {
  const viewport = useRef<HTMLDivElement>(null)
  const [scrollTop, setScrollTop] = useState(0)
  const [height, setHeight] = useState(maxHeight)

  const offsets = useMemo(() => {
    const out = new Array<number>(items.length + 1)
    out[0] = 0
    for (let i = 0; i < items.length; i++) out[i + 1] = out[i] + heightOf(items[i])
    return out
    // heightOf is a pure function of the item
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])

  const total = offsets[items.length] ?? 0

  useEffect(() => {
    const el = viewport.current
    if (!el) return
    const measure = () => setHeight(el.clientHeight || maxHeight)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [maxHeight])

  // Binary search for the first row whose bottom edge is below the fold.
  const findIndex = (y: number) => {
    let lo = 0
    let hi = items.length
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (offsets[mid + 1] <= y) lo = mid + 1
      else hi = mid
    }
    return Math.min(lo, Math.max(0, items.length - 1))
  }

  const first = Math.max(0, findIndex(scrollTop) - overscan)
  const last = Math.min(items.length, findIndex(scrollTop + height) + overscan + 1)
  const visible = items.slice(first, last)

  return (
    <div
      className="bulk-scroll"
      ref={viewport}
      style={{ maxHeight }}
      onScroll={(e) => setScrollTop((e.target as HTMLDivElement).scrollTop)}
      role="table"
      aria-label={label}
      aria-rowcount={items.length}
      tabIndex={0}
    >
      <div style={{ height: total, position: 'relative', minWidth: 900 }}>
        {visible.map((item, i) => {
          const index = first + i
          return (
            <div
              key={index}
              role="row"
              aria-rowindex={index + 1}
              style={{
                position: 'absolute',
                top: offsets[index],
                left: 0,
                right: 0,
                height: offsets[index + 1] - offsets[index],
              }}
            >
              {render(item, index)}
            </div>
          )
        })}
      </div>
    </div>
  )
}
